import React, { useState } from "react";
import { X, Loader2, Building2, Link as LinkIcon, MapPin } from "lucide-react";
import { createJobAPI, updateJobAPI } from "../services/apicollection";

const linesToPayload = (text) =>
  String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const JobForm = ({ initialJob = null, onSuccess, onCancel }) => {
  const isEdit = Boolean(initialJob?._id);

  const [formData, setFormData] = useState({
    positionName: initialJob?.positionName || "",
    jobDescription: initialJob?.jobDescription || "",
    companyName: initialJob?.companyName || "",
    companyLogoUrl: initialJob?.companyLogoUrl || "",
    location: initialJob?.location || "",
    locationType: initialJob?.locationType || "onsite",
    experienceLabel: initialJob?.experienceLabel || "",
    skillsText: (initialJob?.skillsRequired || []).join("\n"),
    responsibilitiesText: (initialJob?.keyResponsibilities || []).join("\n"),
    isPublished: initialJob?.isPublished !== false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.positionName?.trim() ||
      !formData.jobDescription?.trim() ||
      !formData.companyName?.trim() ||
      !formData.location?.trim()
    ) {
      alert("Position, description, company name, and location are required");
      return;
    }

    const payload = {
      positionName: formData.positionName.trim(),
      jobDescription: formData.jobDescription.trim(),
      companyName: formData.companyName.trim(),
      companyLogoUrl: formData.companyLogoUrl?.trim() || "",
      location: formData.location.trim(),
      locationType: formData.locationType,
      experienceLabel: formData.experienceLabel?.trim() || "",
      skillsRequired: linesToPayload(formData.skillsText),
      keyResponsibilities: linesToPayload(formData.responsibilitiesText),
      isPublished: formData.isPublished,
    };

    setSubmitting(true);
    try {
      const res = isEdit
        ? await updateJobAPI(initialJob._id, payload)
        : await createJobAPI(payload);
      if (!res?.status) {
        alert(res?.message || "Failed to save job");
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Failed to save job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-200">
            {isEdit ? "Edit job" : "Post a job"}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Company logo: paste an image URL (no file upload).
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#13233a] text-slate-500 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-400">
            Position title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.positionName}
            onChange={(e) => handleChange("positionName", e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            placeholder="e.g. Full Stack Developer"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Job description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.jobDescription}
            onChange={(e) => handleChange("jobDescription", e.target.value)}
            rows={5}
            className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            placeholder="Overview, requirements, benefits…"
            required
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Company name <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2">
              <Building2 size={16} className="text-slate-400" />
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleChange("companyName", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Company logo URL
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2">
              <LinkIcon size={16} className="text-slate-400" />
              <input
                type="url"
                value={formData.companyLogoUrl}
                onChange={(e) => handleChange("companyLogoUrl", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                placeholder="https://…"
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Location <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2">
              <MapPin size={16} className="text-slate-400" />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange("location", e.target.value)}
                className="h-7 w-full border-none bg-transparent text-sm outline-none"
                placeholder="City or region"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">Location type</label>
            <select
              value={formData.locationType}
              onChange={(e) => handleChange("locationType", e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            >
              <option value="onsite">Onsite</option>
              <option value="hybrid">Hybrid</option>
              <option value="remote">Remote</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">Experience</label>
          <input
            type="text"
            value={formData.experienceLabel}
            onChange={(e) => handleChange("experienceLabel", e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            placeholder="e.g. 1–3 years, Freshers welcome"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Skills required (one per line)
          </label>
          <textarea
            value={formData.skillsText}
            onChange={(e) => handleChange("skillsText", e.target.value)}
            rows={4}
            className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-mono"
            placeholder={"React\nNode.js\nMongoDB"}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Key responsibilities (one per line)
          </label>
          <textarea
            value={formData.responsibilitiesText}
            onChange={(e) => handleChange("responsibilitiesText", e.target.value)}
            rows={4}
            className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-mono"
            placeholder={"Build REST APIs\nCode reviews…"}
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={formData.isPublished}
            onChange={(e) => handleChange("isPublished", e.target.checked)}
            className="rounded border-slate-300"
          />
          Published (visible on public jobs page)
        </label>

        <div className="flex justify-end gap-2 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-4 py-2 text-sm text-slate-300"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {isEdit ? "Update job" : "Create job"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;
